'use client'

import { useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import { mediaFile } from '../../lib/media'

export function PortalImageStack() {
  const containerRef = useRef<HTMLDivElement>(null)
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    const handleScroll = () => {
      const el = containerRef.current
      if (!el) return

      const rect = el.getBoundingClientRect()
      const viewport = window.innerHeight || 1
      const raw = (viewport - rect.top) / (viewport + rect.height)
      const clamped = Math.max(0, Math.min(1, raw))
      setProgress(clamped)
    }

    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleScroll)
    }
  }, [])

  const topY = progress * -24
  const bottomY = progress * 28
  const topScale = 1 + progress * 0.015
  const bottomScale = 1 - progress * 0.015

  return (
    <div
      ref={containerRef}
      className="relative mx-auto max-w-[560px] lg:max-w-none lg:w-[520px]"
    >
      <div className="pointer-events-none absolute -inset-5 bg-radial from-brand-gold-light/15 via-transparent to-transparent blur-2xl" />

      <div
        className="relative z-10 w-[84%] ml-auto rounded-2xl overflow-hidden shadow-[0_20px_55px_rgba(0,0,0,0.42)] ring-1 ring-white/20 rotate-[1deg] transition-transform duration-150"
        style={{ transform: `translateY(${topY}px) scale(${topScale}) rotate(1deg)` }}
      >
        <div className="relative aspect-[16/10]">
          <Image
            src={mediaFile('portal2.png')}
            alt="Client portal case details view"
            fill
            className="object-cover"
            priority
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-black/15 via-transparent to-white/10" />
        </div>
      </div>

      <div
        className="relative z-20 -mt-14 w-[88%] rounded-2xl overflow-hidden shadow-[0_24px_65px_rgba(0,0,0,0.5)] ring-1 ring-white/20 -rotate-[0.75deg] transition-transform duration-150 lg:-mt-16"
        style={{ transform: `translateY(${bottomY}px) scale(${bottomScale}) rotate(-0.75deg)` }}
      >
        <div className="relative aspect-[4/3]">
          <Image
            src={mediaFile('portal1.png')}
            alt="Client portal dashboard overview"
            fill
            className="object-cover"
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-black/20" />
        </div>
      </div>

      <div className="pointer-events-none absolute inset-x-12 -bottom-4 h-10 rounded-full bg-black/35 blur-xl" />
    </div>
  )
}
